import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
    Button,
    Form,
    FormField,
    Icon,
    Textarea,
} from '@amzn/awsui-components-react';

import CancelModal, {
    CancelModalProps,
} from '../../../common/components/CancelModal/CancelModal';
import { containsPII } from '../../utils/piiValidator';
import useFormDirtyState from '../../hooks/useFormDirtyState';

const NotesForm = ({
    testId,
    initialValue,
    setEditing,
    getNotesClickHandler,
    hasPiiValidation = false,
}: {
    testId: string;
    initialValue: string | null;
    setEditing: Dispatch<SetStateAction<boolean>>;
    getNotesClickHandler: (notes: string | null) => () => void;
    hasPiiValidation?: boolean;
}) => {
    const [notesValue, setNotesValue] = useState(initialValue);
    const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);
    const [piiWarning, setPiiWarning] = useState<string>('');
    const isDirty = useFormDirtyState(initialValue, notesValue);

    const cancelModalProps: CancelModalProps = {
        isCancelModalVisible,
        setIsCancelModalVisible,
        onCancelConfirm: () => {
            setEditing(false);
            setPiiWarning('');
        },
        testPrefix: 'EditNotes',
        isDirty,
    };

    useEffect(() => {
        setNotesValue(initialValue);
        setPiiWarning('');
    }, [initialValue, setNotesValue]);

    const handleTextareaChange = (value: string) => {
        setNotesValue(value);

        if (hasPiiValidation && containsPII(value)) {
            setPiiWarning(
                'Please do not include any personal information as they will be redacted (e.g. email, phone, SSN, credit card)',
            );
        } else {
            setPiiWarning('');
        }
    };

    const handleSaveClick = () => {
        getNotesClickHandler(notesValue)();
    };

    return (
        <div>
            <Form
                data-testid="NotesForm"
                actions={
                    <div className="awsui-util-pt-xl">
                        <Button
                            variant="link"
                            data-testid={`${testId}NotesCancelBtn`}
                            onClick={() => {
                                setIsCancelModalVisible(true);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            data-testid={`${testId}NotesSubmitBtn`}
                            onClick={handleSaveClick}
                        >
                            Save
                        </Button>
                    </div>
                }
            >
                <FormField>
                    <Textarea
                        placeholder="Enter a note"
                        value={notesValue}
                        autofocus={true}
                        data-testid={`${testId}NotesTextarea`}
                        onChange={(e) => {
                            handleTextareaChange(e.detail.value);
                        }}
                    />
                    {piiWarning && (
                        <span
                            className="awsui-util-status-warning nowrap"
                            data-testid={`${testId}PiiWarning`}
                        >
                            <Icon name="status-warning" /> {piiWarning}
                        </span>
                    )}
                </FormField>
            </Form>
            <CancelModal {...cancelModalProps} />
        </div>
    );
};

export default NotesForm;
